import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, Sparkles, Loader2, Calendar } from "lucide-react";

export default function RFPAnalyzer() {
  const [templates, setTemplates] = useState([]);
  const [chosenTemplate, setChosenTemplate] = useState("");
  const [autoDraft, setAutoDraft] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(""); // uploading | extracting | drafting
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/documents/templates").then(r => setTemplates(r.data)).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true); setResult(null); setProgress(0); setPhase("uploading");
    const fd = new FormData();
    fd.append("file", file);
    if (chosenTemplate) fd.append("template_doc_id", chosenTemplate);
    fd.append("generate_proposal_now", String(autoDraft));
    try {
      const r = await api.post("/rfp/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          setProgress(pct);
          if (pct >= 100) setPhase(autoDraft ? "drafting" : "extracting");
        },
      });
      setResult(r.data);
      toast.success("RFP analyzed" + (autoDraft ? " · proposal drafted" : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "RFP analysis failed");
    } finally { setUploading(false); setPhase(""); if (inputRef.current) inputRef.current.value = ""; }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="rfp-analyzer-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// RFP INTAKE</div>
        <h1 className="font-display font-black text-4xl tracking-tight">RFP Analyzer</h1>
        <p className="text-slate-400 mt-2">Drop any RFP or solicitation PDF. BidForge extracts requirements and drafts the full proposal for you.</p>
      </div>

      <div className="bg-[#0D1424] border border-white/10 p-5 mb-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="mono-label text-slate-400 mb-2">MODEL AFTER (optional)</div>
          <select value={chosenTemplate} onChange={(e) => setChosenTemplate(e.target.value)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="rfp-template">
            <option value="">— No template · use company profile only —</option>
            {templates.map(t => (
              <option key={t.doc_id} value={t.doc_id}>
                {t.doc_type === "won_proposal" ? "🏆 " : ""}{t.name}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-end gap-2 cursor-pointer">
          <input type="checkbox" checked={autoDraft} onChange={(e) => setAutoDraft(e.target.checked)} className="w-4 h-4 accent-cyan-400" data-testid="rfp-autodraft" />
          <span className="text-sm text-slate-300 pb-1">Auto-generate proposal draft</span>
        </label>
      </div>

      <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={onDrop} className={`bg-[#0D1424] border-2 border-dashed p-14 text-center transition-colors ${uploading ? "border-cyan-400" : "border-white/10 hover:border-cyan-400/50"}`} data-testid="rfp-dropzone">
        <input ref={inputRef} type="file" accept=".pdf,.docx,.txt" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="rfp-input" />
        {uploading ? (
          <div>
            <Loader2 className="w-10 h-10 text-cyan-400 mx-auto mb-4 animate-spin" />
            <div className="mono-label text-cyan-400 mb-3">
              {phase === "uploading" && `UPLOADING · ${progress}%`}
              {phase === "extracting" && "◉ CLAUDE SONNET 4.5 · EXTRACTING REQUIREMENTS"}
              {phase === "drafting" && "◉ WRITING 14-SECTION PROPOSAL · 20-40s"}
            </div>
            {phase === "uploading" && <div className="w-full max-w-md mx-auto h-1 bg-white/5"><div className="h-full bg-cyan-400 transition-all" style={{ width: `${progress}%` }} /></div>}
            {(phase === "extracting" || phase === "drafting") && <div className="text-slate-400 text-xs mt-2">Please don't close this tab.</div>}
          </div>
        ) : (
          <div>
            <UploadCloud className="w-12 h-12 text-cyan-400 mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-xl mb-2">Drop the RFP here</div>
            <div className="text-sm text-slate-400 mb-4">PDF, DOCX, or TXT · up to 25MB</div>
            <button onClick={() => inputRef.current?.click()} className="bg-cyan-400 text-[#070B14] px-6 py-3 mono-label hover:bg-cyan-300" data-testid="rfp-choose">CHOOSE FILE</button>
          </div>
        )}
      </div>

      {result && (
        <div className="mt-8" data-testid="rfp-result">
          <div className="bg-[#0D1424] border border-cyan-400/40 p-6 mb-6">
            <div className="mono-label text-cyan-400 mb-3">◉ REQUIREMENTS EXTRACTED</div>
            <h2 className="font-display font-bold text-2xl mb-3">{result.structured.title}</h2>
            <div className="grid md:grid-cols-3 gap-3 mb-5">
              <Fact label="Agency" value={result.structured.agency} />
              <Fact label="Deadline" value={result.structured.response_deadline} icon={Calendar} />
              <Fact label="NAICS" value={result.structured.naics} />
              <Fact label="Set-Aside" value={result.structured.set_aside} />
              <Fact label="Bonding" value={result.structured.bonding_requirements} />
              <Fact label="Insurance" value={result.structured.insurance_requirements} />
            </div>
            <div className="mono-label text-slate-400 mb-2">SCOPE SUMMARY</div>
            <div className="text-sm text-slate-300 leading-relaxed mb-5">{result.structured.scope_summary}</div>
            <div className="grid md:grid-cols-2 gap-5">
              {result.structured.requirements?.length > 0 && (
                <div>
                  <div className="mono-label text-cyan-400 mb-3">REQUIREMENTS · {result.structured.requirements.length}</div>
                  <ul className="space-y-1.5 text-sm text-slate-300">
                    {result.structured.requirements.map((r, i) => <li key={i} className="flex gap-2"><span className="text-slate-600">→</span>{r}</li>)}
                  </ul>
                </div>
              )}
              {result.structured.compliance_items?.length > 0 && (
                <div>
                  <div className="mono-label text-cyan-400 mb-3">COMPLIANCE ITEMS · {result.structured.compliance_items.length}</div>
                  <ul className="space-y-1.5 text-sm text-slate-300">
                    {result.structured.compliance_items.map((r, i) => <li key={i} className="flex gap-2"><span className="text-slate-600">✓</span>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            {result.proposal_generated
              ? <button onClick={() => nav(`/proposals/${result.notice_id}`)} className="bg-cyan-400 text-[#070B14] px-6 py-3 mono-label hover:bg-cyan-300 flex items-center gap-2" data-testid="open-drafted-proposal"><FileText className="w-4 h-4" />OPEN PROPOSAL DRAFT</button>
              : <button onClick={() => nav(`/opportunities/${result.notice_id}`)} className="bg-cyan-400 text-[#070B14] px-6 py-3 mono-label hover:bg-cyan-300 flex items-center gap-2" data-testid="open-rfp-opp"><Sparkles className="w-4 h-4" />OPEN OPPORTUNITY</button>
            }
          </div>
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, icon: Icon }) {
  return (
    <div className="border border-white/10 p-3">
      <div className="mono-label text-slate-500 mb-1 flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />}{label}</div>
      <div className="text-sm text-white">{value || "—"}</div>
    </div>
  );
}
